import { css } from '@emotion/react';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { semantic_colors, shapes } from '../../foundation';
import { Stack } from '../stack';
import { CategoriesByLevels } from './CategoriesByLevels';
import { CategoryTreeView } from './CategoryTreeView';
import type { Category, CategoryEntry } from './types';
import { getCategoryMaxDepth } from './utils';

interface BaseCategorySelectorProps {
  className?: string;
  selectedKeys: string[];
  /**
   * 선택값 변경 이벤트
   * @param selectedKeys 선택된 카테고리 키 목록
   * @param isLeaf 선택된 카테고리가 리프인지 여부. `multiple`이 `false`일 때만 전달됩니다.
   */
  onChange: (selectedKeys: string[], isLeaf?: boolean) => void;
  /**
   * 체크박스 사용여부
   * true일 경우 멀티 셀렉트 가능
   * @default false
   */
  multiple?: boolean;
  /** @default false */
  disabled?: boolean;
  /**
   * 각 ListItem의 너비
   * @default 240
   */
  width?: number;
  /**
   * 각 List의 높이
   * @default 332
   */
  height?: number;
}

export interface CategoriesByLevelsCategorySelectorProps extends BaseCategorySelectorProps {
  /**
   * 레벨별 카테고리 데이터
   * @deprecated items를 사용해주세요. 이 방식은 향후 제거될 예정입니다.
   */
  categoriesByLevels: Category[][];
  items?: never;
}

export interface ItemsCategorySelectorProps extends BaseCategorySelectorProps {
  /** 트리 구조 카테고리 데이터 */
  items: CategoryEntry[];
  categoriesByLevels?: never;
}

export type CategorySelectorProps = CategoriesByLevelsCategorySelectorProps | ItemsCategorySelectorProps;

function hasCategoriesByLevels(props: CategorySelectorProps): props is CategoriesByLevelsCategorySelectorProps {
  return 'categoriesByLevels' in props;
}

export const CategorySelector = (props: CategorySelectorProps) => {
  if ('items' in props && props.items !== undefined && 'categoriesByLevels' in props && props.categoriesByLevels) {
    throw new Error('CategorySelector: items와 categoriesByLevels를 동시에 사용할 수 없습니다.');
  }

  const { className, selectedKeys, onChange, multiple = false, disabled = false, width = 240, height = 332 } = props;
  const usesCategoriesByLevels = hasCategoriesByLevels(props);
  const categoriesByLevels = usesCategoriesByLevels ? props.categoriesByLevels : undefined;
  const items = useMemo(() => (usesCategoriesByLevels ? [] : props.items), [props, usesCategoriesByLevels]);
  const maxDepth = useMemo(
    () => (!usesCategoriesByLevels && items.length > 0 ? getCategoryMaxDepth(items) : 1),
    [items, usesCategoriesByLevels],
  );

  return (
    <Stack
      direction='row'
      className={classNames(['pds-category-selector', className])}
      width={categoriesByLevels ? width * categoriesByLevels.length : width * maxDepth}
      height={height}
      css={css`
        overflow: hidden;
        border: 1px solid ${semantic_colors.border.primary};
        ${shapes.border_radius.small}
      `}
    >
      {categoriesByLevels ? (
        <CategoriesByLevels
          categoriesByLevels={categoriesByLevels}
          selectedKeys={selectedKeys}
          multiple={multiple}
          disabled={disabled}
          onChange={onChange}
        />
      ) : (
        <CategoryTreeView
          items={items}
          selectedKeys={selectedKeys}
          onChange={onChange}
          multiple={multiple}
          disabled={disabled}
          width={width}
          maxDepth={maxDepth}
        />
      )}
    </Stack>
  );
};
